import { createFileRoute } from '@tanstack/react-router';
import { Separator } from '@vta/ui';
import { OgMeta } from '../lib/og-meta.tsx';

export const Route = createFileRoute('/about')({
  component: AboutRoute,
});

function AboutRoute() {
  return (
    <>
      <OgMeta
        title="About"
        description="Data sources, privacy policy, and disclaimers for VEX Teams Atlas."
        pathname="/about"
      />
      <main className="container mx-auto flex max-w-3xl flex-col gap-10 py-10 md:py-14">
        <header className="flex flex-col gap-2 border-b border-border-subtle pb-6">
          <p className="text-2xs uppercase tracking-[0.3em] text-fg-subtle">About</p>
          <h1 className="font-display text-3xl text-fg sm:text-4xl">
            VEX Teams Atlas
          </h1>
          <p className="text-sm text-fg-muted">
            A read-mostly directory of VEX teams, cities, and events. Built independently —
            not affiliated with VEX Robotics or the REC Foundation.
          </p>
        </header>

        <Section title="Data sources">
          <p>
            Team and event data is sourced from the public RobotEvents directory and
            normalised on a daily sync cadence. We layer on user-submitted team stories
            after admin review.
          </p>
          <ul className="list-disc pl-5 marker:text-fg-subtle">
            <li>RobotEvents public team / event API</li>
            <li>User-submitted stories (admin-reviewed before publishing)</li>
            <li>Geographic clustering via PostGIS-style aggregation in the API</li>
          </ul>
          <p>
            Coverage is best-effort. Division metadata in particular varies by event;
            when a division is not reported for a team, we surface that gap rather than
            hide it.
          </p>
        </Section>

        <Separator />

        <Section title="Privacy">
          <p>
            We collect only the data necessary to operate the directory:
            authentication identifiers from your sign-in provider, and the contents of
            stories or photos you submit.
          </p>
          <p>
            Submitted content is treated as published-on-approval. Team stories are
            visible to anyone once an admin approves them. Removing a story removes it
            from public view but a record is retained for audit.
          </p>
        </Section>

        <Separator />

        <Section title="Disclaimer">
          <p>
            Team rosters, awards, and event metadata are reproduced from upstream
            sources. Errors propagate. If you spot something wrong, please open an
            issue or submit a correction via the team page.
          </p>
          <p>
            VEX, V5RC, VIQRC, VURC, and VAIRC are trademarks of Innovation First, Inc.
            VEX Teams Atlas is an independent project and is not endorsed by or
            affiliated with the REC Foundation or VEX Robotics.
          </p>
        </Section>
      </main>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-xl text-fg">{title}</h2>
      <div className="flex flex-col gap-3 text-sm leading-relaxed text-fg-muted">{children}</div>
    </section>
  );
}
